import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useCallback } from "react"

function createResource(resolver, cacheKeyGenerator) {
  return (...args) => [() => resolver(...args), cacheKeyGenerator(...args)]
}

const SessionApiResource = createResource(
  (pathname, options = {}) =>
    sessionApiClient.get(pathname, {}, options).catch((json) => json),
  (pathname, options = {}) =>
    `SessionApiResource-${pathname}-${JSON.stringify({ options })}`,
)

function useCacheRead(resource, ...resourceArgs) {
  const [queryFn, queryKey] = resource(...resourceArgs)
  const result = useSuspenseQuery({
    queryKey: [queryKey],
    queryFn: queryFn,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  return result.data
}

export function useApiRead(pathname, options = {}) {
  return useCacheRead(SessionApiResource, pathname, options)
}
export function useApiInvalidate(pathname, options = {}) {
  const queryClient = useQueryClient()
  const [_queryFn, queryKey] = SessionApiResource(pathname, options)

  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [queryKey] }),
    [queryClient, queryKey],
  )
}

/**
 * @template {import("./types").BaseAPIRecord} T
 * @param {string} pageOne
 * @param {{}} [options={}]
 * @returns {{ data: T[] }[]}
 */
export function useApiReadEntireCollection(pageOne, options = {}) {
  return useCacheRead(EntireCollectionResource, pageOne, options)
}

export async function getEntireCollection(pageOne, options = {}) {
  let nextLink = pageOne
  let responses = []
  while (nextLink) {
    let json = await sessionApiClient.get(nextLink, {}, options)
    responses.push(json)
    nextLink = json.links?.next
  }
  return responses
}

const EntireCollectionResource = createResource(
  getEntireCollection,
  (pageOne, _options = {}) => `EntireCollectionResource-${pageOne}`,
)

const ParallelApiResource = createResource(
  (...pathnames) =>
    Promise.allSettled(
      pathnames.map((pathname) => sessionApiClient.get(pathname, {}, {})),
    ),
  (...pathnames) => pathnames.join("__AND__"),
)

export function useApiReadMultiple(...pathnames) {
  return useCacheRead(ParallelApiResource, ...pathnames)
}

export const SessionPostApiResource = createResource(
  (pathname, params, options = {}) =>
    sessionApiClient.post(pathname, params, options).catch((json) => json),
  (pathname, params, options = {}) =>
    `SessionApiResource-POST-${pathname}-${JSON.stringify({
      params,
    })}-${JSON.stringify({ options })}`,
)

export function useApiPost(pathname, params = undefined, options = {}) {
  return useCacheRead(SessionPostApiResource, pathname, params, options)
}
